import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  Divider,
  CardActionArea,
  LinearProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import { ExpandMore, ExpandLess, WhatsApp, CloudUpload, InsertDriveFile } from '@mui/icons-material';

const TemplateSelectionDialog = ({
  open,
  templates,
  onClose,
  onConfirm,
  isSending = false,
  sendProgress = 0,
  sendError = null,
  validationError = '',
  setSendError,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [previewContent, setPreviewContent] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [headerFile, setHeaderFile] = useState(null);
  const [headerFilePreview, setHeaderFilePreview] = useState(null);

  // ✅ Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setVariableValues({});
      setFieldErrors({});
      setPreviewContent('');
      setExpandedTemplate(null);
      setHeaderFile(null);
      setHeaderFilePreview(null);
      setSendError(null); // ✅ Clear errors when dialog closes
    }
  }, [open, setSendError]);
const excludedNames = [
  'download_otp_new',
  'download_otp',
  'download_btn_otp',
  'insta_lead_marketing',
  'invoice_new_001',
  'birthday_new',
  'anniversary',
  'appointment_new',
  'latest_whatsapp',
  'invoice_new_08082025',
  'new_invoice_whatsapp',
  'marketing_1',
  'invoice_ys',
  'ai_marketing_ys',
];
  // Process templates from API response - Enhanced to handle buttons
  const processedTemplates = templates
    .filter((template) => !excludedNames.includes(template.name))
    .map((template) => {
      const languageData = template.languages?.[0] || {};
      const bodyComponent = languageData.code?.find((comp) => comp.type === 'BODY');
      const buttonComponents = languageData.code?.filter((comp) => comp.type === 'BUTTONS') || [];

      // Extract button information
      const buttons = buttonComponents.flatMap(
        (buttonComp) =>
          buttonComp.buttons?.map((btn) => ({
            type: btn.type,
            text: btn.text,
            url: btn.url,
            phone_number: btn.phone_number,
            example: btn.example?.[0], // Get first example for preview
          })) || [],
      );

      return {
        id: languageData.id || template.name,
        name: template.name,
        category: template.category,
        status: languageData.status,
        bodyText: bodyComponent?.text || '',
        variables: languageData.variables || [],
        variableTypes: languageData.variable_type || {},
        buttons: buttons,
        // Extract header info for header_1 detection
        headerInfo: languageData.variable_type?.header_1 || null,
      };
    });

  // Validate individual field
  const validateField = (value, variableName) => {
    if (!value || value.trim() === '') {
      return `${variableName} is required`;
    }
    if (value.length > 100) {
      return `${variableName} must be less than 100 characters`;
    }
    return '';
  };

  // Generate real-time preview with buttons
  const generateRealTimePreview = (template, currentValues = {}) => {
    if (!template.bodyText) return 'No body text available';

    let previewText = template.bodyText;

    // Replace variables in body text
    template.variables.forEach((variable, index) => {
      const value = currentValues[`var_${index + 1}`] || `{${variable}}`;
      const regex = new RegExp(`\\{\\{${index + 1}\\}\\}`, 'g');

      if (!currentValues[`var_${index + 1}`]) {
        previewText = previewText.replace(
          regex,
          `<span style="background-color: #ffebee; padding: 2px 4px; border-radius: 3px; border: 1px dashed #f44336; color: #d32f2f;">{${variable}}</span>`,
        );
      } else {
        previewText = previewText.replace(
          regex,
          `<span style="background-color: #e8f5e8; padding: 2px 4px; border-radius: 3px; border: 1px solid #4caf50;">${value}</span>`,
        );
      }
    });

    return previewText;
  };

  // Generate buttons preview
  const generateButtonsPreview = (template, currentValues = {}) => {
    if (!template.buttons || template.buttons.length === 0) return null;

    return template.buttons.map((button, index) => {
      let buttonText = button.text;

      // Replace variables in button text if they exist
      template.variables.forEach((variable, varIndex) => {
        const value = currentValues[`var_${varIndex + 1}`] || button.example || `{${variable}}`;
        const regex = new RegExp(`\\{\\{${varIndex + 1}\\}\\}`, 'g');
        buttonText = buttonText.replace(regex, value);
      });

      return {
        ...button,
        previewText: buttonText,
        displayText: buttonText.length > 20 ? buttonText.substring(0, 20) + '...' : buttonText,
      };
    });
  };

  // Handle variable input change with validation
  const handleVariableChange = (template, variableIndex, value) => {
    const varKey = `var_${variableIndex + 1}`;
    const variableName = template.variables[variableIndex];

    // Validate field
    const error = validateField(value, variableName);
    setFieldErrors((prev) => ({
      ...prev,
      [template.id]: {
        ...prev[template.id],
        [varKey]: error,
      },
    }));

    // Update values
    setVariableValues((prev) => ({
      ...prev,
      [template.id]: {
        ...prev[template.id],
        [varKey]: value,
      },
    }));

    // Update preview
    if (template.id === selectedTemplate?.id) {
      const updatedValues = {
        ...(variableValues[template.id] || {}),
        [varKey]: value,
      };
      const newPreview = generateRealTimePreview(template, updatedValues);
      setPreviewContent(newPreview);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setVariableValues((prev) => ({
      ...prev,
      [template.id]: prev[template.id] || {},
    }));

    // Generate initial preview
    const initialPreview = generateRealTimePreview(template, variableValues[template.id] || {});
    setPreviewContent(initialPreview);

    // Clear previous errors and header file
    setFieldErrors((prev) => ({
      ...prev,
      [template.id]: {},
    }));
    setHeaderFile(null);
    setHeaderFilePreview(null);
  };

  // Check if a variable is a header (non-text) type
  const isHeaderVariable = (variable, template) => {
    const varType = template.variableTypes?.[variable];
    return varType && varType.type !== 'text';
  };

  // Get accepted file types based on header type
  const getAcceptForHeaderType = (type) => {
    switch (type) {
      case 'image': return 'image/*';
      case 'video': return 'video/*';
      case 'document': return '.pdf';
      default: return 'image/*,video/*,.pdf';
    }
  };

  // Handle header file selection
  const handleHeaderFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setHeaderFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setHeaderFilePreview(ev.target.result);
        reader.readAsDataURL(file);
      } else {
        setHeaderFilePreview(null);
      }
    }
  };

  // Handle confirm with validation
  const handleConfirm = () => {
    if (!selectedTemplate) {
      return;
    }

    // Validate all fields (skip header variables — they use file input)
    const currentErrors = {};
    let hasErrors = false;

    selectedTemplate.variables.forEach((variable, index) => {
      if (isHeaderVariable(variable, selectedTemplate)) return; // skip header vars
      const varKey = `var_${index + 1}`;
      const value = variableValues[selectedTemplate.id]?.[varKey] || '';
      const error = validateField(value, variable);

      currentErrors[varKey] = error;
      if (error) hasErrors = true;
    });

    // Validate header file if template has header
    if (selectedTemplate.headerInfo && !headerFile) {
      currentErrors['header_file'] = 'Header file is required';
      hasErrors = true;
    }

    setFieldErrors((prev) => ({
      ...prev,
      [selectedTemplate.id]: currentErrors,
    }));

    if (hasErrors) {
      return;
    }

    // Prepare template with variables and buttons
    const templateWithVariables = {
      ...selectedTemplate,
      variableValues: variableValues[selectedTemplate.id] || {},
      headerFile: headerFile || null, // pass the File object
      buttons: selectedTemplate.buttons
        ? selectedTemplate.buttons.map((button) => ({
            ...button,
          }))
        : [],
    };

    onConfirm(templateWithVariables);
  };

  // Check if template has errors
  const hasTemplateErrors = (template) => {
    if (!fieldErrors[template.id]) return false;
    return Object.values(fieldErrors[template.id]).some((error) => error !== '');
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setVariableValues({});
      setFieldErrors({});
      setPreviewContent('');
      setExpandedTemplate(null);
      setHeaderFile(null);
      setHeaderFilePreview(null);
    }
  }, [open]);

  // Auto-select first template
  useEffect(() => {
    if (open && processedTemplates.length > 0 && !selectedTemplate) {
      handleTemplateSelect(processedTemplates[0]);
      setExpandedTemplate(processedTemplates[0].id);
    }
  }, [open, processedTemplates]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={isSending ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          overflow: 'hidden',
          height: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
          color: 'white',
          textAlign: 'center',
          py: 2,
          position: 'relative',
        }}
      >
        {isSending && (
          <LinearProgress
            variant="determinate"
            value={sendProgress}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white',
              },
            }}
          />
        )}

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <WhatsApp sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight="600">
              WhatsApp Template Manager
              {isSending && ` - Sending... ${sendProgress}%`}
            </Typography>
          </Box>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setSidebarVisible(!sidebarVisible);
            }}
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
            disabled={isSending}
          >
            {sidebarVisible ? 'Hide Templates' : 'Show Templates'}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          {isSending ? 'Sending messages...' : 'Select template and fill all required variables'}
        </Typography>
      </DialogTitle>

      {/* Error Display */}
      {(sendError || validationError) && (
        <Alert
          severity="error"
          sx={{ m: 2, mb: 0 }}
          onClose={() => {
            setSendError(null);
          }}
        >
          {sendError || validationError}
        </Alert>
      )}

      <DialogContent dividers sx={{ p: 0, height: '100%' }}>
        <Box display="flex" height="100%">
          {/* Template List Sidebar */}
          {sidebarVisible && (
            <Box width="30%" sx={{ borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>
              <Box p={2}>
                <Typography variant="h6" gutterBottom color="primary">
                  Templates ({processedTemplates.length})
                </Typography>

                {processedTemplates.map((template) => (
                  <Card
                    key={template.id}
                    sx={{
                      mb: 2,
                      border:
                        selectedTemplate?.id === template.id
                          ? '2px solid #25d366'
                          : '1px solid #e0e0e0',
                      borderColor: hasTemplateErrors(template) ? '#f44336' : undefined,
                      cursor: isSending ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: isSending ? 0.7 : 1,
                      '&:hover': {
                        boxShadow: isSending ? 'none' : '0 4px 12px rgba(37, 211, 102, 0.2)',
                        borderColor: isSending ? 'inherit' : '#25d366',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={isSending ? undefined : () => handleTemplateSelect(template)}
                      disabled={isSending}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                              {template.name}
                              {hasTemplateErrors(template) && (
                                <span style={{ color: '#f44336', marginLeft: '8px' }}>•</span>
                              )}
                            </Typography>
                            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                              <Chip
                                label={template.category}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                label={template.status}
                                size="small"
                                color={template.status.toUpperCase() === 'APPROVED' ? 'success' : 'warning'}
                              />
                              <Chip
                                label={`${template.variables.length} vars`}
                                size="small"
                                variant="outlined"
                                color={template.variables.length > 0 ? 'primary' : 'default'}
                              />
                              {template.buttons && template.buttons.length > 0 && (
                                <Chip
                                  label={`${template.buttons.length} buttons`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTemplate(
                                expandedTemplate === template.id ? null : template.id,
                              );
                            }}
                            disabled={isSending}
                          >
                            {expandedTemplate === template.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>

                        <Collapse in={expandedTemplate === template.id}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                          >
                            {template.bodyText.substring(0, 150)}...
                          </Typography>
                          {template.buttons && template.buttons.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary">
                                Buttons: {template.buttons.map((btn) => btn.text).join(', ')}
                              </Typography>
                            </Box>
                          )}
                        </Collapse>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {/* Main Content Area */}
          <Box width={sidebarVisible ? '70%' : '100%'} sx={{ overflowY: 'auto' }}>
            {selectedTemplate ? (
              <Box p={3}>
                <Typography variant="h6" gutterBottom color="primary">
                  {selectedTemplate.name} - Configuration
                </Typography>

                <Grid container spacing={3}>
                  {/* Variables Input Section */}
                  <Grid item xs={12} md={6}>
                    <Box mb={3}>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#666', mb: 2 }}>
                        Template Variables{' '}
                        {selectedTemplate.variables.length > 0 && '(All fields are required)'}
                      </Typography>

                      {selectedTemplate.variables.length === 0 ? (
                        <Alert severity="info">This template doesn't require any variables.</Alert>
                      ) : (
                        <>
                          {/* Header file upload — shown if template has header_1 */}
                          {selectedTemplate.headerInfo && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ color: '#555', fontWeight: 600 }}>
                                Header Media ({selectedTemplate.headerInfo.type})
                              </Typography>
                              <Box
                                sx={{
                                  border: headerFile
                                    ? '2px solid #4caf50'
                                    : fieldErrors[selectedTemplate.id]?.header_file
                                      ? '2px dashed #f44336'
                                      : '2px dashed #ccc',
                                  borderRadius: '12px',
                                  p: 2,
                                  textAlign: 'center',
                                  cursor: isSending ? 'default' : 'pointer',
                                  bgcolor: headerFile ? '#f1f8e9' : '#fafafa',
                                  transition: 'all 0.2s ease',
                                  '&:hover': isSending ? {} : { borderColor: '#25d366', bgcolor: '#f9fbe7' },
                                }}
                                onClick={() => !isSending && document.getElementById('header-file-input')?.click()}
                              >
                                <input
                                  id="header-file-input"
                                  type="file"
                                  accept={getAcceptForHeaderType(selectedTemplate.headerInfo.type)}
                                  style={{ display: 'none' }}
                                  onChange={handleHeaderFileChange}
                                  disabled={isSending}
                                />
                                {headerFile ? (
                                  <Box>
                                    {headerFilePreview ? (
                                      <img
                                        src={headerFilePreview}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '8px', marginBottom: '8px' }}
                                      />
                                    ) : (
                                      <InsertDriveFile sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                                    )}
                                    <Typography variant="body2" color="success.main" fontWeight={600}>
                                      {headerFile.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {(headerFile.size / 1024).toFixed(1)} KB — Click to change
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box>
                                    <CloudUpload sx={{ fontSize: 40, color: '#bbb', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      Click to upload {selectedTemplate.headerInfo.type}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              {fieldErrors[selectedTemplate.id]?.header_file && (
                                <FormHelperText error>
                                  {fieldErrors[selectedTemplate.id].header_file}
                                </FormHelperText>
                              )}
                            </Box>
                          )}

                          {/* Text variables (skip header variables) */}
                          {selectedTemplate.variables
                            .filter((variable) => !isHeaderVariable(variable, selectedTemplate))
                            .map((variable) => {
                              const originalIndex = selectedTemplate.variables.indexOf(variable);
                              const value =
                                variableValues[selectedTemplate.id]?.[`var_${originalIndex + 1}`] || '';
                              const error =
                                fieldErrors[selectedTemplate.id]?.[`var_${originalIndex + 1}`] || '';
                              const isButton = variable.toLowerCase().includes('button');

                              return (
                                <Box key={`var-${originalIndex}`} sx={{ mb: 2 }}>
                                  <TextField
                                    fullWidth
                                    label={`${variable} ${isButton ? '(Button)' : ''}`}
                                    variant="outlined"
                                    value={value}
                                    onChange={(e) =>
                                      handleVariableChange(selectedTemplate, originalIndex, e.target.value)
                                    }
                                    placeholder={`Enter value for ${variable}`}
                                    error={!!error}
                                    disabled={isSending}
                                    onBlur={(e) => {
                                      const error = validateField(e.target.value, variable);
                                      setFieldErrors((prev) => ({
                                        ...prev,
                                        [selectedTemplate.id]: {
                                          ...prev[selectedTemplate.id],
                                          [`var_${originalIndex + 1}`]: error,
                                        },
                                      }));
                                    }}
                                  />
                                  {error && <FormHelperText error>{error}</FormHelperText>}
                                </Box>
                              );
                            })}
                        </>
                      )}
                    </Box>
                  </Grid>

                  {/* Real-time Preview Section */}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#666', mb: 2 }}>
                        Live Preview:
                      </Typography>

                      <Card
                        sx={{
                          bgcolor: '#e5ddd5',
                          border: '1px solid #e0e0e0',
                          p: 3,
                          minHeight: '200px',
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: 'white',
                            borderRadius: '7.5px',
                            padding: '12px 16px',
                            maxWidth: '70%',
                            boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              left: '-8px',
                              top: '0',
                              width: '0',
                              height: '0',
                              borderTop: '0 solid transparent',
                              borderBottom: '8px solid transparent',
                              borderRight: '8px solid white',
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            dangerouslySetInnerHTML={{ __html: previewContent }}
                            sx={{ lineHeight: 1.4 }}
                          />
                        </Box>

                        {/* Buttons Preview */}
                        {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
                          <Box mt={2}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={1}
                            >
                              Buttons Preview:
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                              {generateButtonsPreview(
                                selectedTemplate,
                                variableValues[selectedTemplate.id] || {},
                              ).map((button, index) => (
                                <Button
                                  key={index}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    borderColor: '#25d366',
                                    color: '#25d366',
                                    '&:hover': {
                                      backgroundColor: '#25d366',
                                      color: 'white',
                                    },
                                  }}
                                  style={{
                                    width: '150px',
                                    textAlign: 'center',
                                  }}
                                >
                                  {button.previewText}
                                </Button>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Card>

                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">
                          💡 Red highlighted fields are required. Green fields are filled.
                          {selectedTemplate.buttons &&
                            selectedTemplate.buttons.length > 0 &&
                            ' Buttons will use the variable values you enter.'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                flexDirection="column"
                color="text.secondary"
              >
                <WhatsApp sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  {sidebarVisible ? 'Select a Template' : 'Templates Hidden'}
                </Typography>
                <Typography variant="body2" textAlign="center">
                  {sidebarVisible
                    ? 'Choose a template from the left to configure your message'
                    : 'Click "Show Templates" to view available templates'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            px: 3,
            py: 1,
          }}
          disabled={isSending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!selectedTemplate || isSending || hasTemplateErrors(selectedTemplate)}
          variant="contained"
          sx={{
            background: isSending ? '#ccc' : 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
            borderRadius: '8px',
            px: 3,
            py: 1,
            '&:hover': isSending
              ? {}
              : {
                  background: 'linear-gradient(135deg, #128c7e 0%, #25d366 100%)',
                },
            '&:disabled': {
              background: '#ccc',
            },
          }}
        >
          {isSending ? `Sending... ${sendProgress}%` : 'Use Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectionDialog;